import * as THREE from 'three';
import { Course } from './Course';
import { Hole } from './Hole';
import { VegetationField } from './Vegetation';
import { WaterFeature } from './WaterFeature';
import { Biome } from './Biome';
import { LIGHTING_PRESETS } from '../lighting/presets';
import type { Region } from '../utils/geometry';
import type { CourseDocument, HoleDocument } from './CourseDocument';
import { collidersFromPlacements } from '../simulation/ObstacleCollision';
import { createDemoCourse } from './data/demoCourse';

const FALLBACK_REGION: Region = { kind: 'circle', center: { x: 0, z: 100 }, radius: 20 };
const v3 = (p: { x: number; y?: number; z: number }) => new THREE.Vector3(p.x, p.y ?? 0, p.z);

export function compileCourseDocument(document: CourseDocument): Course {
  const waterFeatures: WaterFeature[] = [];
  const holes = document.holes.map((hole) => compileHoleDocument(hole, waterFeatures));
  const vegetation = new VegetationField();
  for (const hole of document.holes) {
    for (const tree of hole.vegetation)
      vegetation.add({
        modelId: 'tree.conifer.proxy',
        position: v3(tree.position),
        rotationY: tree.rotationY ?? 0,
        scale: tree.scale ?? 1,
      });
    for (const rock of hole.props.filter((p) => p.kind === 'rock'))
      vegetation.add({
        modelId: 'rock.proxy',
        position: v3(rock.position),
        rotationY: rock.rotationY ?? 0,
        scale: rock.scale ?? 1,
      });
  }
  const obstacleColliders = document.holes.flatMap((hole) =>
    collidersFromPlacements([...hole.vegetation, ...hole.props]),
  );
  const preset =
    LIGHTING_PRESETS[document.environmentPreset as keyof typeof LIGHTING_PRESETS] ??
    LIGHTING_PRESETS.afternoon;
  return new Course({
    metadata: { id: document.id, name: document.name, description: document.description },
    holes,
    vegetation,
    biomes: [
      new Biome({
        id: 'editor-rough',
        speciesMix: [{ modelId: 'grass.long.proxy', weight: 1 }],
        density: 0.004,
        seed: 7,
        scaleVariation: { min: 0.8, max: 1.2 },
        rotationVariation: true,
      }),
    ],
    waterFeatures,
    lighting: preset.lighting,
    environment: preset.environment,
    colorGrade: preset.colorGrade,
    authoredTerrain: document.terrain,
    obstacleColliders,
  });
}

function compileHoleDocument(hole: HoleDocument, waterFeatures: WaterFeature[]): Hole {
  const waterHazardIds = hole.waterRegions.map((water, index) => {
    const id = `${hole.id}-water-${index + 1}`;
    waterFeatures.push(
      new WaterFeature({
        id,
        kind: 'pond',
        waterLevel: water.waterLevel ?? 0.04,
        region: water.region,
      }),
    );
    return id;
  });
  return new Hole({
    number: hole.number,
    par: hole.par,
    yardage: hole.yardage,
    teeBoxes: hole.teeBoxes.map((tee) => ({ name: tee.name, position: v3(tee.position) })),
    pins: hole.pins.map((pin) => ({ label: pin.label, position: v3(pin.position) })),
    activePinIndex: hole.activePinIndex,
    fairway: hole.fairways[0]?.region ?? FALLBACK_REGION,
    green: hole.greens[0]?.region ?? FALLBACK_REGION,
    rough: hole.roughRegions.map((r) => r.region),
    bunkers: hole.bunkers.map((r) => r.region),
    waterHazardIds,
    outOfBounds: [],
    vegetationIds: hole.vegetation.map((v) => v.id),
  });
}

export function createDemoCourseDocument(): CourseDocument {
  const course = createDemoCourse();
  return {
    schemaVersion: 2,
    id: course.metadata.id,
    name: course.metadata.name,
    description: course.metadata.description ?? '',
    environmentPreset: 'afternoon',
    holes: course.holes.map((hole) => ({
      id: `hole-${hole.number}`,
      number: hole.number,
      par: hole.par,
      yardage: hole.yardage,
      name: `Hole ${hole.number}`,
      teeBoxes: hole.def.teeBoxes.map((tee, i) => ({
        id: `tee-${hole.number}-${i}`,
        name: tee.name,
        position: { x: tee.position.x, y: tee.position.y, z: tee.position.z },
      })),
      pins: hole.def.pins.map((pin, i) => ({
        id: `pin-${hole.number}-${i}`,
        label: pin.label,
        position: { x: pin.position.x, y: pin.position.y, z: pin.position.z },
      })),
      activePinIndex: hole.def.activePinIndex,
      fairways: [{ id: `fairway-${hole.number}`, region: hole.def.fairway }],
      greens: [{ id: `green-${hole.number}`, region: hole.def.green }],
      roughRegions: hole.def.rough.map((region, i) => ({
        id: `rough-${hole.number}-${i}`,
        region,
      })),
      bunkers: hole.def.bunkers.map((region, i) => ({ id: `bunker-${hole.number}-${i}`, region })),
      waterRegions: course.waterFeatures
        .filter((water) => hole.def.waterHazardIds.includes(water.def.id))
        .map((water, i) => ({ id: `water-${hole.number}-${i}`, region: water.def.region })),
      vegetation: [],
      props: [],
    })),
  };
}
